/**
 * Session context + hooks.
 *
 * `useSession()` returns the current user (null if unauthenticated),
 * `useLogin()` / `useLogout()` mutate it, and the provider listens for the
 * `REAUTH_EVENT` dispatched by the axios interceptor to flip back to the
 * login screen if Gigafy's token can't be refreshed.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";

import { api, asApiError } from "../api/client";

export type Me = {
  gigafy_username: string;
  csrf_token: string;
  token_expires_at: number;
  is_api_key_admin?: boolean;
};

type SessionContextValue = {
  user: Me | null;
  isLoading: boolean;
  isReauthNeeded: boolean;
};

const SessionContext = createContext<SessionContextValue>({
  user: null,
  isLoading: true,
  isReauthNeeded: false,
});

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery<Me | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        const res = await api.get<Me>("/auth/me");
        return res.data;
      } catch (err) {
        const { status } = asApiError(err);
        if (status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });

  // NOTE: REAUTH_EVENT is handled by <ReauthModal /> (mounted in the
  // protected layout). The modal asks for a password without bouncing
  // the user off the current page; only after a "Sign out" click does
  // it redirect to /login.
  useEffect(() => {
    void queryClient;
    void navigate;
  }, [queryClient, navigate]);

  return (
    <SessionContext.Provider
      value={{
        user: data ?? null,
        isLoading,
        isReauthNeeded: false,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  return useContext(SessionContext);
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (creds: {
      username: string;
      password: string;
      // Opt-in 30-day sliding session (SESSION_REMEMBER_TTL_SECONDS).
      remember_me?: boolean;
    }) => {
      const res = await api.post<Me>("/auth/login", creds);
      return res.data;
    },
    onSuccess: (me) => {
      queryClient.setQueryData(["me"], me);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async () => {
      await api.post("/auth/logout");
    },
    onSettled: () => {
      queryClient.setQueryData(["me"], null);
      queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate("/login");
    },
  });
}
